import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Input, Spin, message } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';

interface CaptchaInputProps {
  value?: string;
  onChange?: (value: string) => void;
  captchaId?: string;
  onCaptchaIdChange?: (id: string) => void;
  disabled?: boolean;
  placeholder?: string;
  refreshTrigger?: number; // 外部触发刷新的计数器
}

interface CaptchaResponse {
  success: boolean;
  data?: {
    captchaId: string;
    captchaImage: string;
  };
  error?: {
    code: string;
    message: string;
  };
}

const CaptchaInput: React.FC<CaptchaInputProps> = ({
  value,
  onChange,
  onCaptchaIdChange,
  disabled = false,
  placeholder = '请输入验证码',
  refreshTrigger = 0,
}) => {
  const [captchaImage, setCaptchaImage] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const isMounted = useRef(true);
  const lastRefreshTrigger = useRef(refreshTrigger);

  // 使用 ref 保存回调函数，避免依赖变化
  const onChangeRef = useRef(onChange);
  const onCaptchaIdChangeRef = useRef(onCaptchaIdChange);
  onChangeRef.current = onChange;
  onCaptchaIdChangeRef.current = onCaptchaIdChange;

  // 获取验证码
  const fetchCaptcha = async () => {
    if (disabled || loading) return;

    setLoading(true);
    try {
      const response = await fetch('/api/v1/auth/captcha', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const data: CaptchaResponse = await response.json();

      if (!isMounted.current) return;

      if (data.success && data.data) {
        setCaptchaImage(data.data.captchaImage);
        onCaptchaIdChangeRef.current?.(data.data.captchaId);
        // 清空输入
        onChangeRef.current?.('');
      } else {
        message.error(data.error?.message || '获取验证码失败');
      }
    } catch (error) {
      console.error('Failed to fetch captcha:', error);
      if (isMounted.current) {
        message.error('获取验证码失败，请检查网络连接');
      }
    } finally {
      if (isMounted.current) {
        setLoading(false);
      }
    }
  };

  // 组件挂载时获取验证码
  useEffect(() => {
    isMounted.current = true;
    fetchCaptcha();
    return () => {
      isMounted.current = false;
    };
  }, []);

  // 外部触发刷新
  useEffect(() => {
    if (refreshTrigger !== lastRefreshTrigger.current) {
      lastRefreshTrigger.current = refreshTrigger;
      fetchCaptcha();
    }
  }, [refreshTrigger]);

  // 刷新验证码
  const handleRefresh = () => {
    fetchCaptcha();
  };

  // 处理输入变化 - 验证码只接受字母和数字，直接过滤
  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      // 从原始输入中提取字母和数字，转大写
      const rawValue = e.target.value;
      const newValue = rawValue.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
      onChange?.(newValue);
    },
    [onChange]
  );

  return (
    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
      <Input
        value={value}
        onChange={handleInputChange}
        placeholder={placeholder}
        maxLength={4}
        disabled={disabled}
        style={{ flex: 1, textTransform: 'uppercase' }}
        autoComplete="off"
        inputMode="text"
      />
      <div
        onClick={handleRefresh}
        style={{
          width: '120px',
          height: '40px',
          cursor: disabled ? 'not-allowed' : 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          border: '1px solid #d9d9d9',
          borderRadius: '6px',
          overflow: 'hidden',
          backgroundColor: '#f5f5f5',
        }}
        title="点击刷新验证码"
      >
        {loading ? (
          <Spin size="small" />
        ) : captchaImage ? (
          <img
            src={captchaImage}
            alt="验证码"
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'contain',
            }}
          />
        ) : (
          <ReloadOutlined style={{ fontSize: '20px', color: '#999' }} />
        )}
      </div>
    </div>
  );
};

export default CaptchaInput;
